/**
 * A customer's household (the owner, 2026-10-03): the customer themself and the people at the door — mother, father,
 * wife, brother… — with a date of birth, alive or not, an anniversary, and whether they may take the bottles.
 *
 *   · MemberRows: the wizard's rows, held in the form until Review (saved by api.onboard_customer in one call);
 *   · HouseholdPanel: Customer 360 › Family — the list, add, edit, remove (api.customer_members / save / remove).
 * A date of birth and a phone are personal data: the server hides them from someone who may not see them.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cake, Heart, Plus, Trash2, UserRound } from 'lucide-react';
import { rpc } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { Button, EmptyState, ErrorState, LoadingRows, Panel, PanelHeader, cx, plainError } from '../../ui/primitives';
import { Checkbox, Field, Input, Select } from '../../ui/forms';
import { toast } from '../../ui/toast';
import * as f from '../../ui/format';

export interface Relation { code: string; label: string; labelHi?: string | null }
export interface MemberDraft {
  id?: string; name: string; relation: string; dateOfBirth: string; isAlive: boolean; anniversary: string; mayReceive: boolean; phone: string; gender: string;
}
export const EMPTY_MEMBER: MemberDraft = { name: '', relation: '', dateOfBirth: '', isAlive: true, anniversary: '', mayReceive: false, phone: '', gender: '' };
interface Member {
  id: string; name: string; relation: string; relationLabel: string | null; isAlive: boolean; gender: string | null; mayReceive: boolean; note: string | null;
  dateOfBirth: string | null; age: number | null; birthdayMonthDay: string | null; anniversary: string | null; anniversaryMonthDay: string | null; phone: string | null;
}

export function useRelations() {
  return useQuery({ queryKey: ['customer-relations'], queryFn: () => rpc<Relation[]>('customer_relations'), staleTime: 300_000 });
}

/** One member's fields; used by the wizard's rows and the 360's add/edit form. */
function MemberFields({ m, set, relations, withPhone = true, allowSelf = false }: { m: MemberDraft; set: (patch: Partial<MemberDraft>) => void; relations: Relation[]; withPhone?: boolean; allowSelf?: boolean }) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <Field label="Name" required><Input value={m.name} onChange={(e) => set({ name: e.target.value })} /></Field>
      <Field label="Relation" required><Select value={m.relation} onChange={(e) => set({ relation: e.target.value })} placeholder="Choose"
        options={relations.filter((r) => allowSelf || r.code !== 'self' || m.relation === 'self').map((r) => ({ value: r.code, label: r.label }))} /></Field>
      <Field label="Gender"><Select value={m.gender} onChange={(e) => set({ gender: e.target.value })} placeholder="Not said"
        options={[{ value: 'FEMALE', label: 'Female' }, { value: 'MALE', label: 'Male' }, { value: 'OTHER', label: 'Other' }]} /></Field>
      <Field label="Date of birth"><Input type="date" value={m.dateOfBirth} onChange={(e) => set({ dateOfBirth: e.target.value })} /></Field>
      <Field label="Anniversary" hint="Wedding anniversary, if married"><Input type="date" value={m.anniversary} onChange={(e) => set({ anniversary: e.target.value })} /></Field>
      {withPhone ? <Field label="Phone"><Input value={m.phone} onChange={(e) => set({ phone: e.target.value })} inputMode="tel" /></Field> : null}
      <div className="grid content-end gap-1 pb-1">
        <Checkbox checked={m.isAlive} onChange={(e) => set({ isAlive: e.target.checked })} label="With us" />
        <Checkbox checked={m.mayReceive} onChange={(e) => set({ mayReceive: e.target.checked })} label="May take the bottles" />
      </div>
    </div>
  );
}

/** The wizard: the customer's own date of birth and anniversary, then the family, row by row. */
export function MemberRows({ selfDob, selfAnniversary, selfGender, onSelf, rows, onRows }: {
  selfDob: string; selfAnniversary: string; selfGender: string; onSelf: (patch: { dob?: string; anniversary?: string; gender?: string }) => void;
  rows: MemberDraft[]; onRows: (rows: MemberDraft[]) => void;
}) {
  const relations = useRelations();
  const setRow = (i: number, patch: Partial<MemberDraft>) => onRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="grid gap-3">
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="Customer's gender"><Select value={selfGender} onChange={(e) => onSelf({ gender: e.target.value })} placeholder="Not said"
          options={[{ value: 'FEMALE', label: 'Female' }, { value: 'MALE', label: 'Male' }, { value: 'OTHER', label: 'Other' }]} /></Field>
        <Field label="Customer's date of birth"><Input type="date" value={selfDob} onChange={(e) => onSelf({ dob: e.target.value })} /></Field>
        <Field label="Customer's anniversary"><Input type="date" value={selfAnniversary} onChange={(e) => onSelf({ anniversary: e.target.value })} /></Field>
      </div>
      {rows.map((m, i) => (
        <div key={i} className="rounded-md border border-line p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-small font-medium text-ink-muted">Family member {i + 1}</span>
            <Button size="dense" onClick={() => onRows(rows.filter((_, j) => j !== i))}><Trash2 size={14} aria-hidden /> Remove</Button>
          </div>
          <MemberFields m={m} set={(p) => setRow(i, p)} relations={relations.data ?? []} />
        </div>))}
      <div><Button size="dense" onClick={() => onRows([...rows, { ...EMPTY_MEMBER }])}><Plus size={14} aria-hidden /> Add a family member</Button></div>
    </div>
  );
}

/** Customer 360 › Family. */
export function HouseholdPanel({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const q = useQuery({ queryKey: ['customer-members', customerId],
    queryFn: () => rpc<{ members: Member[]; relations: Relation[]; personalData: boolean }>('customer_members', { p_customer_id: customerId }) });
  const [editing, setEditing] = useState<MemberDraft | null>(null);
  const may = can('customer.edit') && !!q.data?.personalData;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['customer-members', customerId] }); void qc.invalidateQueries({ queryKey: ['crm-occasions'] }); };
  const save = useMutation({
    mutationFn: (m: MemberDraft) => rpc<{ detail: string }>('save_customer_member', { p_customer_id: customerId, p_member: {
      ...m, dateOfBirth: m.dateOfBirth || null, anniversary: m.anniversary || null, phone: m.phone || null }, p_reason: 'Household updated at the desk' }),
    onSuccess: (r) => { toast(r.detail, { tone: 'ok' }); setEditing(null); refresh(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => rpc<{ detail: string }>('remove_customer_member', { p_member_id: id, p_reason: 'Removed from the household at the desk' }),
    onSuccess: (r) => { toast(r.detail); refresh(); },
  });
  const edit = (m: Member) => setEditing({ id: m.id, name: m.name, relation: m.relation, dateOfBirth: m.dateOfBirth ?? '', isAlive: m.isAlive,
                                         anniversary: m.anniversary ?? '', mayReceive: m.mayReceive, phone: m.phone ?? '', gender: m.gender ?? '' });
  return (
    <Panel>
      <PanelHeader title="Family" note="The people of this household: birthdays and anniversaries reach CRM › Overview."
        actions={may && !editing ? <Button size="dense" onClick={() => setEditing({ ...EMPTY_MEMBER })}><Plus size={14} aria-hidden /> Add a member</Button> : undefined} />
      {q.error ? <ErrorState error={q.error} retry={() => void q.refetch()} /> : !q.data ? <LoadingRows rows={3} columns={4} /> : (
        <>
          {editing ? (
            <div className="grid gap-3 border-b border-line px-4 py-3">
              {editing.relation === 'self'
                ? <p className="text-small text-ink-muted">The customer themself.</p> : null}
              <MemberFields m={editing} set={(p) => setEditing({ ...editing, ...p })} relations={q.data.relations}
                allowSelf={!q.data.members.some((x) => x.relation === 'self' && x.id !== editing.id)} />
              {save.error ? <p role="alert" className="text-small text-refused">{plainError((save.error as Error).message)}</p> : null}
              <div className="flex justify-end gap-2">
                <Button onClick={() => setEditing(null)}>Cancel</Button>
                <Button variant="primary" disabled={save.isPending || !editing.name.trim() || !editing.relation} onClick={() => save.mutate(editing)}>Save</Button>
              </div>
            </div>) : null}
          {q.data.members.length === 0 ? <EmptyState title="No family yet" detail="Add the customer's own birthday, and the family's, to remember their days." /> : (
            <ul className="divide-y divide-line">
              {q.data.members.map((m) => (
                <li key={m.id} className="flex items-start justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <div className={cx('font-medium', !m.isAlive && 'text-ink-muted')}>
                      <UserRound size={14} className="mr-1 inline text-ink-faint" aria-hidden />{m.name}
                      <span className="ml-2 text-small text-ink-muted">{m.relationLabel ?? m.relation}{m.gender ? ` · ${m.gender === 'FEMALE' ? 'female' : m.gender === 'MALE' ? 'male' : 'other'}` : ''}{m.isAlive ? '' : ' · passed away'}</span>
                      {m.mayReceive ? <span className="ml-2 text-small text-info">may take the bottles</span> : null}
                    </div>
                    <div className="text-small text-ink-muted">
                      {m.birthdayMonthDay ? <><Cake size={12} className="mr-1 inline" aria-hidden />{m.dateOfBirth ? f.date(m.dateOfBirth) : m.birthdayMonthDay}{m.age !== null ? ` (${m.age})` : ''}</> : null}
                      {m.anniversaryMonthDay ? <span className="ml-3"><Heart size={12} className="mr-1 inline" aria-hidden />{m.anniversary ? f.date(m.anniversary) : m.anniversaryMonthDay}</span> : null}
                      {m.phone ? <span className="ml-3">{m.phone}</span> : null}
                    </div>
                  </div>
                  {may ? <div className="flex shrink-0 gap-1">
                    <Button size="dense" onClick={() => edit(m)}>Edit</Button>
                    <Button size="dense" onClick={() => remove.mutate(m.id)} disabled={remove.isPending} aria-label={`Remove ${m.name}`}><Trash2 size={14} aria-hidden /></Button>
                  </div> : null}
                </li>))}
            </ul>)}
        </>)}
    </Panel>
  );
}
